import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { ApiError } from "../api/client";

type ToastKind = "ok" | "error" | "info";

interface Toast {
  id: number;
  kind: ToastKind;
  text: string;
}

interface ToastsValue {
  notify: (text: string, kind?: ToastKind) => void;
  /** Оборачивает действие: показывает сообщение об ошибке вместо падения экрана. */
  run: (action: () => Promise<unknown>, okText?: string) => Promise<boolean>;
}

const ToastsContext = createContext<ToastsValue | null>(null);
const LIFETIME_MS = 5000;

export function ToastsProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const notify = useCallback((text: string, kind: ToastKind = "info") => {
    const id = nextId.current++;
    setToasts(current => [...current, { id, kind, text }]);
    setTimeout(() => setToasts(current => current.filter(toast => toast.id !== id)), LIFETIME_MS);
  }, []);

  const run = useCallback(
    async (action: () => Promise<unknown>, okText?: string) => {
      try {
        await action();
        if (okText) notify(okText, "ok");
        return true;
      } catch (error) {
        if (error instanceof ApiError && error.code === "unauthorized") return false;
        notify(error instanceof Error ? error.message : "Не удалось выполнить действие", "error");
        return false;
      }
    },
    [notify]
  );

  const value = useMemo<ToastsValue>(() => ({ notify, run }), [notify, run]);

  return (
    <ToastsContext.Provider value={value}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map(toast => (
          <div key={toast.id} className={`toast ${toast.kind}`}>
            {toast.text}
          </div>
        ))}
      </div>
    </ToastsContext.Provider>
  );
}

export function useToasts(): ToastsValue {
  const value = useContext(ToastsContext);
  if (!value) throw new Error("useToasts вызван вне ToastsProvider");
  return value;
}
